import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { getGameColor, getGameName } from "../constants/theme";
import { ShareProfileButton } from "../components/ShareProfileButton";
import { toast } from "sonner";
import {
  User, ArrowLeft, Heart, HeartOff, MessageSquare, MapPin, Gamepad2, Store,
  Instagram, Facebook, Youtube, Globe, MessageCircle, Loader2, Trophy, Sparkles, Layers, Medal,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const absUrl = (u) => (!u ? null : (u.startsWith('http') || u.startsWith('data:') ? u : `${API_URL}${u}`));

const ROLE_LABELS = { PLAYER: "Joueur", ORG: "Organisateur", ADMIN: "Admin", VISITOR: "Joueur" };

const fmtFormat = (key) => {
  const part = key.includes(":") ? key.split(":")[1] : key;
  return part.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};
const formatGame = (key) => (key.includes(":") ? key.split(":")[0] : null);

export default function PublicUserProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [results, setResults] = useState([]);
  const [matchStats, setMatchStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadProfile(); /* eslint-disable-next-line */ }, [id]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/users/${id}/public`);
      setProfile(res.data);
      api.get(`/users/${id}/results?limit=50`).then((r) => setResults(r.data || [])).catch(() => setResults([]));
      api.get(`/users/${id}/match-stats`).then((r) => setMatchStats(r.data)).catch(() => setMatchStats(null));
    } catch { toast.error("Profil introuvable"); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" /></div>
  );

  if (!profile) return (
    <div className="text-center py-20">
      <User className="w-12 h-12 text-[#555] mx-auto mb-3" />
      <p className="text-[#888]">Profil introuvable</p>
      <Button variant="outline" onClick={() => navigate(-1)} className="mt-4 text-[#B0B0B0] border-[#333]"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
    </div>
  );

  const cover = absUrl(profile.banner_url);
  const avatar = absUrl(profile.avatar_url);
  const displayName = profile.pseudo || profile.name || "Joueur";

  const socials = [
    profile.website && { key: "web", href: profile.website.startsWith("http") ? profile.website : `https://${profile.website}`, Icon: Globe, color: "#4ECDC4", label: "Site web" },
    profile.instagram && { key: "ig", href: profile.instagram.startsWith("http") ? profile.instagram : `https://instagram.com/${profile.instagram.replace("@", "")}`, Icon: Instagram, color: "#E1306C", label: "Instagram" },
    profile.facebook && { key: "fb", href: profile.facebook.startsWith("http") ? profile.facebook : `https://facebook.com/${profile.facebook}`, Icon: Facebook, color: "#1877F2", label: "Facebook" },
    profile.youtube && { key: "yt", href: profile.youtube.startsWith("http") ? profile.youtube : `https://youtube.com/${profile.youtube}`, Icon: Youtube, color: "#FF0000", label: "YouTube" },
    profile.discord_group_link && { key: "dc", href: profile.discord_group_link, Icon: MessageCircle, color: "#5865F2", label: "Discord" },
  ].filter(Boolean);

  return (
    <div className="max-w-5xl mx-auto pb-10" data-testid="public-user-profile">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#888] hover:text-white mb-4 transition-colors" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      {/* Cover */}
      <div className="w-full h-36 sm:h-52 relative overflow-hidden rounded-2xl bg-[#1a1a1a]" data-testid="player-cover">
        {cover ? <img src={cover} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : <div className="w-full h-full" style={{ background: "linear-gradient(135deg,#1a1a1a,#FF6B3522)" }} />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/70 to-transparent" />
      </div>

      {/* Identity */}
      <div className="px-2 sm:px-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 relative z-10">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 border-[#0a0a0a] bg-[#252525] overflow-hidden shrink-0 shadow-xl" data-testid="player-avatar">
            {avatar ? <img src={avatar} alt={displayName} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#FF6B35] text-3xl font-black">{displayName.charAt(0).toUpperCase()}</div>}
          </div>
          <div className="flex-1 min-w-0 pb-1">
            <h1 className="text-white text-2xl sm:text-3xl font-black tracking-tight" data-testid="player-name">{displayName}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <Badge className="text-xs bg-[#FF6B35]/15 text-[#FF6B35]">{ROLE_LABELS[profile.role] || "Joueur"}</Badge>
              {profile.is_arcanien && (
                <Badge className="text-xs bg-[#FFD700]/15 text-[#FFD700] gap-1" data-testid="player-arcanien-badge"><Sparkles className="w-3 h-3" />Arcanien</Badge>
              )}
              {profile.city && <span className="inline-flex items-center gap-1 text-[#B0B0B0] text-sm"><MapPin className="w-3.5 h-3.5" />{profile.city}</span>}
            </div>
          </div>
          <div className="flex gap-2 pb-1">
            {isAuthenticated && user?.id !== id && (
              <button
                onClick={() => navigate(`/messages?to=${id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white text-sm font-medium transition-colors"
                data-testid="player-message-btn"
              >
                <MessageCircle className="w-4 h-4" />Message
              </button>
            )}
            <ShareProfileButton url={`${window.location.origin}/user/${id}`} title={`Profil de ${displayName}`} />
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-8">
          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-4">
            {/* Stats */}
            <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5 flex items-center gap-4" data-testid="player-stats">
              <div className="w-12 h-12 rounded-xl bg-[#FF6B35]/15 flex items-center justify-center shrink-0"><Trophy className="w-6 h-6 text-[#FF6B35]" /></div>
              <div>
                <p className="text-white text-2xl font-black leading-none" data-testid="player-tournaments-count">{profile.tournaments_played ?? 0}</p>
                <p className="text-[#888] text-sm mt-1">Tournoi{(profile.tournaments_played ?? 0) > 1 ? "s" : ""} joué{(profile.tournaments_played ?? 0) > 1 ? "s" : ""}</p>
              </div>
            </div>

            {/* Parties (player-event matches) win/loss */}
            {matchStats && matchStats.total > 0 && (
              <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="player-match-stats">
                <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Gamepad2 className="w-4 h-4 text-[#7B68EE]" />Parties entre joueurs</h2>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-green-400 text-xl font-black" data-testid="match-won">{matchStats.won}</p><p className="text-[#888] text-xs mt-0.5">Gagnées</p></div>
                  <div><p className="text-red-400 text-xl font-black" data-testid="match-lost">{matchStats.lost}</p><p className="text-[#888] text-xs mt-0.5">Perdues</p></div>
                  <div><p className="text-[#7B68EE] text-xl font-black" data-testid="match-winrate">{matchStats.win_rate}%</p><p className="text-[#888] text-xs mt-0.5">Ratio</p></div>
                </div>
              </div>
            )}

            {/* About */}
            <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5">
              <h2 className="text-white font-semibold mb-3">À propos</h2>
              {profile.bio ? <p className="text-[#B0B0B0] text-sm leading-relaxed whitespace-pre-line" data-testid="player-bio">{profile.bio}</p> : <p className="text-[#666] text-sm italic">Aucune présentation pour le moment.</p>}
              {profile.role === "ORG" && profile.company_name && (
                <div className="flex items-center gap-2.5 mt-4 text-[#B0B0B0] text-sm"><Store className="w-4 h-4 text-[#FF6B35]" />{profile.company_name}</div>
              )}
            </div>

            {/* Socials (30% larger icons) */}
            {socials.length > 0 && (
              <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="player-socials">
                <h2 className="text-white font-semibold mb-3">Réseaux</h2>
                <div className="flex flex-wrap items-center gap-2.5">
                  {socials.map(({ key, href, Icon, color, label }) => (
                    <a key={key} href={href} target="_blank" rel="noreferrer" title={label}
                      className="w-12 h-12 rounded-lg bg-[#252525] hover:bg-[#333] flex items-center justify-center text-white transition-colors"
                      style={{ "--hover": color }} data-testid={`player-social-${key}`}>
                      <Icon className="w-6 h-6" style={{ color }} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </aside>

          {/* Content */}
          <section className="lg:col-span-8 space-y-4">
            {/* Games */}
            <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="player-games">
              <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Gamepad2 className="w-4 h-4 text-[#7B68EE]" />Jeux</h2>
              {profile.games_played?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.games_played.map((g) => (
                    <Badge key={g} className="text-sm px-3 py-1" style={{ backgroundColor: `${getGameColor(g)}20`, color: getGameColor(g) }}>{getGameName(g)}</Badge>
                  ))}
                </div>
              ) : <p className="text-[#666] text-sm italic">Aucun jeu renseigné.</p>}
            </div>

            {/* Formats */}
            <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="player-formats">
              <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Layers className="w-4 h-4 text-[#4ECDC4]" />Formats</h2>
              {profile.formats_played?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.formats_played.map((f) => {
                    const g = formatGame(f);
                    const c = g ? getGameColor(g) : "#FF6B35";
                    return <Badge key={f} className="text-sm px-3 py-1" style={{ backgroundColor: `${c}18`, color: c }}>{fmtFormat(f)}</Badge>;
                  })}
                </div>
              ) : <p className="text-[#666] text-sm italic">Aucun format renseigné.</p>}
            </div>

            {/* Recent results / placements */}
            <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="player-results">
              <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Medal className="w-4 h-4 text-[#FFD700]" />Derniers résultats</h2>
              {results.length > 0 ? (
                <>
                  {(() => {
                    const totW = results.reduce((s, r) => s + (r.match_wins || 0), 0);
                    const totL = results.reduce((s, r) => s + (r.match_losses || 0), 0);
                    const totD = results.reduce((s, r) => s + (r.match_draws || 0), 0);
                    const totalMatches = totW + totL + totD;
                    const winRate = totalMatches > 0 ? Math.round((totW / totalMatches) * 100) : 0;
                    const bestRank = Math.min(...results.map((r) => r.rank));
                    return (
                      <div className="grid grid-cols-2 gap-3 mb-4" data-testid="player-result-stats">
                        <div className="rounded-lg bg-[#252525] p-3 text-center">
                          <p className="text-[#4CAF50] text-2xl font-black leading-none" data-testid="player-winrate">{winRate}%</p>
                          <p className="text-[#888] text-xs mt-1">Ratio de victoires</p>
                          <p className="text-[#555] text-[10px] mt-0.5">{totW}V · {totL}D{totD ? ` · ${totD}N` : ''}</p>
                        </div>
                        <div className="rounded-lg bg-[#252525] p-3 text-center">
                          <p className="text-[#FFD700] text-2xl font-black leading-none" data-testid="player-bestrank">#{bestRank}</p>
                          <p className="text-[#888] text-xs mt-1">Meilleur classement</p>
                          <p className="text-[#555] text-[10px] mt-0.5">sur {results.length} tournoi{results.length > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="space-y-2">
                  {results.slice(0, 10).map((r) => {
                    const podium = r.rank === 1 ? "#FFD700" : r.rank === 2 ? "#C0C0C0" : r.rank === 3 ? "#CD7F32" : null;
                    return (
                      <div key={r.tournament_id}
                        onClick={() => navigate(`/player/event/tournament/${r.tournament_id}`)}
                        className="flex items-center gap-3 bg-[#252525] hover:bg-[#2c2c2c] rounded-lg px-3 py-2.5 cursor-pointer transition-colors"
                        data-testid={`player-result-${r.tournament_id}`}>
                        <div className="shrink-0 w-10 h-10 rounded-lg flex flex-col items-center justify-center font-black"
                          style={{ backgroundColor: podium ? `${podium}22` : "#33333a", color: podium || "#B0B0B0" }}>
                          <span className="text-[9px] font-semibold leading-none opacity-70">#{r.rank}</span>
                          <span className="text-[10px] leading-none mt-0.5">/{r.total_players}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-medium truncate">{r.tournament_name}</p>
                          <div className="flex items-center gap-2 text-xs text-[#888] mt-0.5 flex-wrap">
                            <span style={{ color: getGameColor(r.game) }}>{getGameName(r.game)}</span>
                            {r.format && <span>· {r.format}</span>}
                            <span>· {new Date(r.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}</span>
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-xs text-[#B0B0B0] font-semibold">{r.match_wins}-{r.match_losses}{r.match_draws ? `-${r.match_draws}` : ""}</p>
                          <p className="text-[10px] text-[#666]">{r.match_points} pts</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </>
              ) : <p className="text-[#666] text-sm italic">Aucun résultat de tournoi pour le moment.</p>}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
